import React, { useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";

import { Button } from "components/common/Button";
import { InlineError } from "components/common/InlineError";
import { TextInput } from "components/common/TextInput";
import { useConnectionsStore } from "stores/connectionsStore";
import type { SavedConnection } from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";
import { parseSupabaseConnectionString } from "lib/parseSupabaseConnectionString";
import {
  caCertForSubmit,
  Field,
  MAX_NAME_LEN,
  PgCredentialFields,
  SslSection,
  type PgFormState,
} from "components/main/utils/settings/connectionForm";

// Supabase enforces SSL but serves a CA the system trust store doesn't carry,
// so "Encrypt only" (no verify) is the zero-friction default. Database/port
// default to the session-pooler norms.
const emptyForm = (): PgFormState => ({
  display_name: "",
  host: "",
  port: 5432,
  database: "postgres",
  username: "",
  password: "",
  ssl_mode: "Encrypt",
  ca_cert_pem: "",
  clear_ca_cert: false,
});

interface Props {
  onSaved: (saved: SavedConnection) => void;
  onCancel: () => void;
  hideCancel?: boolean;
}

export const SupabaseConnectionEditor: React.FC<Props> = ({
  onSaved,
  onCancel,
  hideCancel = false,
}) => {
  const [form, setForm] = useState<PgFormState>(emptyForm());
  const [connString, setConnString] = useState("");
  const [parseNote, setParseNote] = useState<string | null>(null);
  const [parseWarn, setParseWarn] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready =
    form.display_name.trim().length > 0 &&
    form.host.length > 0 &&
    form.database.length > 0 &&
    form.username.length > 0 &&
    form.port > 0 &&
    form.password.length > 0;

  const update = (patch: Partial<PgFormState>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const handleConnString = (raw: string) => {
    setConnString(raw);
    setParseNote(null);
    setParseWarn(null);
    if (raw.trim().length === 0) return;
    const parsed = parseSupabaseConnectionString(raw);
    if (!parsed) {
      setParseWarn(
        "Couldn't read that — paste the URI from Supabase → Connect, or fill the fields below.",
      );
      return;
    }
    update({
      host: parsed.host,
      port: parsed.port,
      database: parsed.database,
      username: parsed.username,
      ...(parsed.password ? { password: parsed.password } : {}),
    });
    setParseNote(
      parsed.password
        ? "Filled host, port, database, username and password below."
        : "Filled host, port, database and username — enter your password below.",
    );
    if (parsed.hostKind === "unknown") {
      setParseWarn(
        "That host doesn't look like Supabase. Double-check it's the session-pooler host.",
      );
    } else if (parsed.hostKind === "direct") {
      setParseWarn(
        "Direct host detected — it's IPv6-only and often unreachable. The session pooler host is more reliable.",
      );
    }
  };

  const handleSubmit = async () => {
    setPending(true);
    setError(null);
    try {
      const saved = await useConnectionsStore.getState().create({
        display_name: form.display_name.trim(),
        host: form.host,
        port: form.port,
        database: form.database,
        username: form.username,
        password: form.password,
        ssl_mode: form.ssl_mode,
        ca_cert_pem: caCertForSubmit(form),
        connection_type: "supabase",
      });
      onSaved(saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save connection");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {error && <InlineError>{error}</InlineError>}

      <label className="flex flex-col gap-1 min-w-0">
        <span className="text-fg-muted-medium">Paste from Supabase</span>
        <TextInput
          value={connString}
          onChange={handleConnString}
          placeholder="postgresql://postgres.[ref]:[password]@aws-1-…pooler.supabase.com:5432/postgres"
          autoComplete="off"
        />
        {parseNote && (
          <span className="flex items-center gap-1 text-success">
            <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
            {parseNote}
          </span>
        )}
        {parseWarn && <span className="text-warning">{parseWarn}</span>}
      </label>

      <div className="flex items-center gap-2 text-fg-muted-regular-small">
        <span className="flex-1 h-px bg-border" />
        or fill in manually
        <span className="flex-1 h-px bg-border" />
      </div>

      <Field
        label="Name"
        value={form.display_name}
        onChange={(v) => update({ display_name: v.slice(0, MAX_NAME_LEN) })}
        placeholder="Prod readonly"
      />
      <PgCredentialFields
        form={form}
        update={update}
        hostPlaceholder="aws-1-us-east-1.pooler.supabase.com"
        hostInfo="Use the Session pooler host from Supabase → Connect. Avoid the IPv6-only db.*.supabase.co direct host."
        portInfo="5432 for the session pooler (recommended). 6543 is transaction mode."
        usernameInfo="For Supabase this is postgres.<your-project-ref>, not just postgres."
        usernameInfoWarning
        passwordLabel="Password"
      />
      <SslSection
        form={form}
        update={update}
        caHelpText="Optional. Download your project's CA from Supabase → Database settings to verify the server certificate."
      />

      <div className="flex justify-end gap-2 mt-2">
        {!hideCancel && (
          <Button
            variant="secondary"
            size="md"
            onClick={onCancel}
            disabled={pending}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          size="md"
          loading={pending}
          disabled={!ready}
          onClick={handleSubmit}
        >
          Test &amp; save
        </Button>
      </div>
    </div>
  );
};
